import { BACKEND_DATE_FORMATS, UTCMoment } from 'src/utils/UTCMoment';
import { FormSettingsData, TFormSettingsDataProps } from '../../../FormSettingsData';
import React, { useContext, useRef, useState } from 'react';
import {
  selectActiveConfigurationJobUuid,
  selectAssetsValues,
  selectCommunityAsset,
  selectCommunityName,
  selectConfigurationCharacteristic,
  selectReadOnly,
  selectSettingsData,
  selectSimulationResults,
  selectSimulationStatus,
} from 'src/redux/configuration/configuration.selectors';
import {
  selectCommunityNotFound,
  selectIsEmbed,
  selectSidebarExpanded,
} from 'src/redux/application/application.selectors';

import { ApplicationContext } from 'src/contexts/ApplicationContext';
import { BaseButton } from '../../../BaseButton';
import { BaseButtonSquare } from 'src/components/BaseButtonSquare';
import { EDomIds } from 'src/constants/domSelectors';
import { EPredefinedModalIds } from 'src/constants/modals';
import { SimulationResultsPresentation } from 'src/components/SimulationResultsPresentation';
import { TMapSidebarResultsProps } from './MapSidebarResults.types';
import { TextBrick } from 'src/components/TextBrick';
import classnames from 'classnames';
import { openModal } from 'src/redux/modals/modals.slice';
import s from './MapSidebarResults.module.scss';
import { selectIsLoggedIn } from 'src/redux/auth/auth.selectors';
import { setSidebarExpanded } from 'src/redux/application/application.slice';
import { useAppDispatch } from 'src/redux/store';
import useDownloadAPI from 'src/hooks/useDownloadAPI';
import { useSelector } from 'react-redux';
import { v4 } from 'uuid';

export const MapSidebarResults: React.FC<TMapSidebarResultsProps> = ({
  onSettingsDataSave,
  onCommunityRemove,
  showHeaderTabs = true,
}) => {
  const dispatch = useAppDispatch();
  const isEmbed = useSelector(selectIsEmbed);
  const loggedIn = useSelector(selectIsLoggedIn);
  const assetsValues = useSelector(selectAssetsValues);
  const communityName = useSelector(selectCommunityName);
  const communityAsset = useSelector(selectCommunityAsset);
  const sidebarExpanded = useSelector(selectSidebarExpanded);
  const simulationResults = useSelector(selectSimulationResults);
  const activeConfigurationJobUuid = useSelector(selectActiveConfigurationJobUuid);
  const communityNotFound = useSelector(selectCommunityNotFound);
  const readOnly = useSelector(selectReadOnly);
  const formHasErrorsRef = useRef(false);
  const formId = useRef(v4());
  const { triggerResultsLossAlert, triggerCommunityDeleteAlert } = useContext(ApplicationContext);
  const simulationStatus = useSelector(selectSimulationStatus);
  const settingsData = useSelector(selectSettingsData);
  const configurationCharacteristic = useSelector(selectConfigurationCharacteristic);
  const resultsDownloadAPI = useDownloadAPI({
    type: 'simulation-results',
    resultsUuid: activeConfigurationJobUuid,
    startDate: UTCMoment.fromBackend(
      settingsData.startDate,
      BACKEND_DATE_FORMATS.SETTINGS_DATA,
    ).format(BACKEND_DATE_FORMATS.DOWNLOAD_SIM_RESULTS),
    endDate: UTCMoment.fromBackend(settingsData.endDate, BACKEND_DATE_FORMATS.SETTINGS_DATA).format(
      BACKEND_DATE_FORMATS.DOWNLOAD_SIM_RESULTS,
    ),
  });

  const [activeTab, setActiveTab] = useState<'community' | 'settings'>('community');

  const toggleSidebarExpand = () => {
    dispatch(setSidebarExpanded(!sidebarExpanded));
  };

  const handleSettingsDataSave: TFormSettingsDataProps['onSubmit'] = async (payload) => {
    await triggerResultsLossAlert();

    onSettingsDataSave?.(payload);
  };

  const deleteCommunity = async () => {
    await triggerCommunityDeleteAlert();

    onCommunityRemove?.({ projectUuid: configurationCharacteristic.projectUuid });
  };

  return (
    <div className={s.container} id={EDomIds.MODAL_MAP_SIDEBAR}>
      <>
        {showHeaderTabs && (
          <>
            {!loggedIn && (
              <div className={s.header}>
                <h3>{communityName}</h3>
              </div>
            )}
            {loggedIn && (
              <div className={s.header}>
                <ul className={s.headerNav}>
                  <li className={classnames({ [s.active]: activeTab === 'community' })}>
                    <button type="button" onClick={() => setActiveTab('community')}>
                      {communityName}
                    </button>
                  </li>
                  {!isEmbed && (
                    <li className={classnames({ [s.active]: activeTab === 'settings' })}>
                      <button type="button" onClick={() => setActiveTab('settings')}>
                        Settings
                      </button>
                    </li>
                  )}
                </ul>
                <BaseButtonSquare
                  theme="flat-gray"
                  icon={sidebarExpanded ? 'collapse' : 'expand-2'}
                  size="2"
                  svgSize="3"
                  onClick={toggleSidebarExpand}
                />
              </div>
            )}
          </>
        )}
        {simulationStatus === 'finished' && (
          <div className={s.boxSidebarAlert}>
            <div className={s.iconCanary}>
              <BaseButtonSquare
                className={s.iconCanary}
                style={{ maxWidth: 26, maxHeight: 25 }}
                theme="flat-blue"
                icon="canary"
              />
            </div>
            <div className={s.boxText}>
              <h3>Bring your simulation closer to reality</h3>
              <p>Connect assets, use real data and trade</p>
            </div>
            <BaseButton
              onClick={() => dispatch(openModal(EPredefinedModalIds.MODAL_NETWORK_CANARY))}
              type="submit"
              className={s.buttonGo}
              size="normal">
              GO
            </BaseButton>
          </div>
        )}
        <div className={s.header}>
          <h3>
            {simulationResults && simulationResults?.assetUuid !== communityAsset?.uuid
              ? assetsValues[simulationResults.assetUuid]?.name
              : 'Community'}
          </h3>
        </div>
        {/*
            Used conditional rendering instead of switching "display: none",
            because there is some issues with d3 charts when they are not visible.
            https://gridsingularity.atlassian.net/browse/PH-464
        */}
        {activeTab !== 'settings' ? (
          <div className={s.graphsList}>
            {typeof simulationStatus === 'undefined' ? (
              <div className={s.noResults}>
                <TextBrick>{communityNotFound ? 'Community not found' : 'No results'}</TextBrick>
              </div>
            ) : (
              <SimulationResultsPresentation
                simulationResults={simulationResults}
                jobId={activeConfigurationJobUuid}
                settingsData={settingsData}
              />
            )}
          </div>
        ) : (
          <div className={s.settingsList}>
            <FormSettingsData
              className={s.formMaxHeight}
              id={formId.current}
              hasErrorsRef={formHasErrorsRef}
              onSubmit={handleSettingsDataSave}
              disableLocationField={false}
              theme="dark"
            />
            <div className={s.formButtonsWrapper}>
              <BaseButton
                type="submit"
                className={s.formButton}
                form={formId.current}
                disabled={readOnly}>
                Save
              </BaseButton>
            </div>
            <div>
              <BaseButton
                className={classnames(s.settingsActionButton, s.settingsActionButtonBorder)}
                onClick={async () => {
                  resultsDownloadAPI.handleDownload();
                }}
                isLoading={resultsDownloadAPI.loading}
                disabled={simulationStatus !== 'finished'}
                icon={'download'}>
                Download
              </BaseButton>
              <BaseButton
                className={s.settingsActionButton}
                onClick={deleteCommunity}
                icon={'custom-trash'}
                disabled={readOnly}>
                Delete Community
              </BaseButton>
            </div>
          </div>
        )}
      </>
    </div>
  );
};
